import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Chip,
  Stack,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { OverAllLoader } from "../../components/OverAllLoader";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Premium color palette
const themeColors = (() => {
  const primary = "#1976d2"; // your main primary color
  const surface = "#ffffff";
  const background = "#fafbfe";

  // Helper to lighten/darken a hex color
  const adjustColor = (color, percent) => {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      "#" +
      (
        0x1000000 +
        (R < 255 ? (R < 0 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 0 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 0 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  };

  return {
    primary: primary,
    primaryLight: adjustColor(primary, 40),
    primaryDark: adjustColor(primary, -20),
    success: "#2e7d32",
    successLight: "#e8f5e9",
    error: "#d32f2f",
    errorLight: "#ffebee",
    warning: "#ed6c02",
    background: background,
    surface: surface,
    textPrimary: "#1a237e",
    textSecondary: "#5f6368",
    border: "#e0e0e0",

    // Gradient based on primary
    gradient: `linear-gradient(135deg, #1976d2 0%, ${alpha(
      "#1976d2",
      0.8
    )} 100%)`,
    cardGradient: `linear-gradient(145deg, ${surface} 0%, ${background} 100%)`,
  };
})();

const MarkdownQuestion = ({ text }) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => (
          <Typography
            sx={{
              fontSize: { xs: "15px", md: "16px" },
              fontWeight: 600,
              lineHeight: 1.6,
              color: themeColors.textPrimary,
              mb: 1.5,
            }}
          >
            {children}
          </Typography>
        ),
        ol: ({ children }) => (
          <Box
            component="ol"
            sx={{
              pl: { xs: 2.5, md: 3 },
              mb: 1.5,
              mt: 1,
              fontSize: { xs: "15px", md: "16px" },
              lineHeight: 1.7,
            }}
          >
            {children}
          </Box>
        ),
        li: ({ children }) => (
          <Typography
            component="li"
            sx={{
              mb: 1,
              lineHeight: 1.7,
              color: themeColors.textPrimary,
            }}
          >
            {children}
          </Typography>
        ),
        code({ inline, children }) {
          const value = String(children);
          const isBlock = !inline && value.includes("\n");

          if (!isBlock) {
            return (
              <Box
                component="span"
                sx={{
                  backgroundColor: "#d8d9ddff",
                  color: themeColors.primaryDark,
                  borderRadius: "6px",
                  fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                  fontSize: { xs: "13px", md: "14px" },
                  px: 1,
                  py: 0.3,
                  mx: 0.5,
                }}
              >
                {value}
              </Box>
            );
          }

          return (
            <Box
              sx={{
                backgroundColor: "#1e293b",
                color: "#f1f5f9",
                p: { xs: 1.5, md: 2 },
                my: 2,
                borderRadius: "8px",
                overflowX: "auto",
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                fontSize: { xs: "12px", md: "13px" },
                border: "1px solid #334155",
              }}
            >
              <pre style={{ margin: 0 }}>
                <code>{value}</code>
              </pre>
            </Box>
          );
        },
      }}
    >
      {text}
    </ReactMarkdown>
  );
};

export const PreviewTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { assessment_Id } = location?.state || {};
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const userData = JSON.parse(localStorage.getItem("userData"));
  const userId = userData?.user_id;

  const [listData, setListData] = useState({});
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const previewList = async () => {
    setLoading(true);
    try {
      const payload = {
        assessment_id: assessment_Id,
        user_id: userId,
      };

      const res = await api.post("/ListPreviewQuestionPapers", payload);

      if (res?.data?.status === "Success") {
        setListData(res?.data?.data);
      } else {
        showSnackbar("error", res?.data?.message || "Something went wrong!");
      }
    } catch (error) {
      console.error(error);
      showSnackbar("error", "Failed fetching data!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    previewList();
  }, []);

  const attemptsArray = Object.entries(listData);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Container
      maxWidth="xl"
      sx={{ py: isMobile ? 1 : 3, px: isMobile ? 1 : 2 }}
    >
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "70vh",
          }}
        >
          <OverAllLoader message="Fetching preview..." />
        </Box>
      ) : (
        <Box>
          {/* Back Navigation */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            onClick={() => (
              window.history.back(), localStorage.removeItem("TabValue")
            )}
            sx={{
              mb: isMobile ? 2 : 3,
              cursor: "pointer",
              width: "fit-content",
              p: 1,
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.04)",
              },
            }}
          >
            <ArrowBackIcon
              color="primary"
              sx={{ fontSize: isMobile ? 16 : 20 }}
            />
            <Typography
              color="primary"
              sx={{
                fontWeight: 600,
                fontSize: isMobile ? 13 : 14,
              }}
            >
              Back
            </Typography>
          </Stack>

          {/* Header Section */}
          <Box
            sx={{
              mb: isMobile ? 3 : 4,
              p: isMobile ? 2 : 3,
              borderRadius: "16px",
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
              border: "1px solid rgba(25, 118, 210, 0.1)",
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
            }}
          >
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={isMobile ? 2 : 0}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={isMobile ? 1.5 : 2}
              >
                <Box
                  sx={{
                    p: isMobile ? 1.5 : 2,
                    borderRadius: "12px",
                    background: `linear-gradient(135deg, #1976d2 0%, ${alpha(
                      "#1976d2",
                      0.8
                    )} 100%)`,
                    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)",
                  }}
                >
                  <DescriptionOutlinedIcon
                    sx={{ fontSize: isMobile ? 24 : 32, color: "white" }}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: isMobile ? 20 : 28,
                      fontWeight: 800,
                      background:
                        "linear-gradient(135deg, #1976d2 30%, #0d47a1 90%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Question Papers Preview
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: isMobile ? 13 : 14,
                      color: "text.secondary",
                      mt: 0.5,
                      maxWidth: isMobile ? "100%" : "600px",
                    }}
                  >
                    View all generated question paper previews. Expand any
                    preview to see detailed questions and feedback.
                  </Typography>
                </Box>
              </Stack>

              {/* Action Buttons */}
              <Stack
                direction={isMobile ? "column" : "row"}
                spacing={isMobile ? 1 : 1.5}
              >
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    navigate("/preview", {
                      state: { assessment_Id: assessment_Id, initial: true },
                    })
                  }
                  className="gradient-button"
                >
                  New Preview
                </Button>
              </Stack>
            </Stack>

            {/* Stats Info */}
            {attemptsArray.length > 0 && !isMobile && (
              <Stack
                direction="row"
                spacing={3}
                sx={{
                  mt: 3,
                  pt: 2,
                  borderTop: "1px solid rgba(25, 118, 210, 0.1)",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CalendarTodayIcon
                    sx={{ fontSize: 16, color: "primary.main" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Total Previews:{" "}
                    <Typography
                      component="span"
                      color="primary"
                      fontWeight="600"
                    >
                      {attemptsArray.length}
                    </Typography>
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <QuestionAnswerOutlinedIcon
                    sx={{ fontSize: 16, color: "primary.main" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Total Questions:{" "}
                    <Typography
                      component="span"
                      color="primary"
                      fontWeight="600"
                    >
                      {attemptsArray.reduce(
                        (total, [_, obj]) =>
                          total + (obj?.questions?.length || 0),
                        0
                      )}
                    </Typography>
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Box>

          {/* No Data State */}
          {attemptsArray.length === 0 && !loading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "60vh",
                textAlign: "center",
                px: isMobile ? 2 : 0,
              }}
            >
              <Box
                sx={{
                  width: isMobile ? 120 : 160,
                  height: isMobile ? 120 : 160,
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                <DescriptionOutlinedIcon
                  sx={{
                    fontSize: isMobile ? 48 : 64,
                    color: "rgba(25, 118, 210, 0.3)",
                  }}
                />
              </Box>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: "text.primary",
                }}
              >
                No Previews Generated Yet
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  mb: 3,
                  maxWidth: "400px",
                }}
              >
                Start by creating your first question paper preview to see it
                listed here.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() =>
                  navigate("/preview", {
                    state: { assessment_Id: assessment_Id, initial: true },
                  })
                }
                className="gradient-button"
              >
                Create First Preview
              </Button>
            </Box>
          )}

          {/* Previews List */}
          {attemptsArray.length > 0 && (
            <Box>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{
                  fontWeight: 700,
                  mb: isMobile ? 2 : 3,
                  color: "text.primary",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <InfoOutlinedIcon
                  color="primary"
                  fontSize={isMobile ? "small" : "medium"}
                />
                Generated Previews ({attemptsArray.length})
              </Typography>

              {attemptsArray.map(([attemptNo, obj], index) => (
                <Accordion
                  key={attemptNo}
                  defaultExpanded={index === 0 && !isMobile}
                  sx={{
                    mb: isMobile ? 2 : 3,
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
                    border: "1px solid rgba(25, 118, 210, 0.12)",
                    "&:before": { display: "none" },
                    "&.Mui-expanded": {
                      margin: isMobile ? "16px 0" : "24px 0",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon
                        sx={{
                          color: "primary.main",
                          fontSize: isMobile ? 20 : 24,
                        }}
                      />
                    }
                    sx={{
                      p: isMobile ? 2 : 3,
                      backgroundColor:
                        index % 2 === 0
                          ? "rgba(25, 118, 210, 0.02)"
                          : "#ffffff",
                      borderBottom: "1px solid rgba(25, 118, 210, 0.08)",
                      minHeight: "72px",
                      "&.Mui-expanded": { minHeight: "72px" },
                    }}
                  >
                    <Grid
                      container
                      alignItems="center"
                      spacing={isMobile ? 1 : 2}
                    >
                      <Grid item xs={12} sm={6} md={4}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                        >
                          <Box
                            sx={{
                              width: isMobile ? 32 : 40,
                              height: isMobile ? 32 : 40,
                              borderRadius: "10px",
                              background: `linear-gradient(135deg, #1976d2 0%, ${alpha(
                                "#1976d2",
                                0.8
                              )} 100%)`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                color: "white",
                                fontWeight: 700,
                                fontSize: isMobile ? 14 : 16,
                              }}
                            >
                              {index + 1}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontSize: isMobile ? 16 : 18,
                                fontWeight: 700,
                                color: "text.primary",
                              }}
                            >
                              Preview {obj?.preview_no}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <AccessTimeIcon sx={{ fontSize: 12 }} />
                              Generated on {formatDate(obj?.created_at)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent={isMobile ? "flex-start" : "center"}
                        >
                          <Chip
                            label={`${obj?.questions?.length || 0} Questions`}
                            size="small"
                            icon={
                              <QuestionAnswerOutlinedIcon fontSize="small" />
                            }
                            sx={{
                              backgroundColor: "rgba(25, 118, 210, 0.1)",
                              color: "primary.main",
                              fontWeight: 600,
                            }}
                          />
                          {obj?.status && (
                            <Chip
                              label={obj.status}
                              size="small"
                              sx={{
                                backgroundColor:
                                  obj.status === "Completed"
                                    ? "rgba(76, 175, 80, 0.1)"
                                    : "rgba(255, 152, 0, 0.1)",
                                color:
                                  obj.status === "Completed"
                                    ? "#4caf50"
                                    : "#ff9800",
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Stack>
                      </Grid>

                      <Grid item xs={12} sm={12} md={4}>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent={isMobile ? "flex-start" : "flex-end"}
                        >
                          <Button
                            variant="outlined"
                            size={isMobile ? "small" : "medium"}
                            startIcon={<VisibilityIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate("/preview", {
                                state: { gen_Id: obj?.gen_id, fromView: true },
                              });
                            }}
                            sx={{
                              borderRadius: "8px",
                              textTransform: "none",
                              fontWeight: 600,
                              borderWidth: "1.5px",
                              "&:hover": {
                                borderWidth: "1.5px",
                                backgroundColor: "rgba(25, 118, 210, 0.04)",
                              },
                            }}
                          >
                            View Details
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{ p: isMobile ? 2 : 3, backgroundColor: "#fafcff" }}
                  >
                    {/* Empty State for Questions */}
                    {!obj?.questions || obj?.questions?.length === 0 ? (
                      <Box
                        sx={{
                          p: isMobile ? 3 : 4,
                          my: 2,
                          borderRadius: "12px",
                          background:
                            "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                          border: "2px dashed rgba(25, 118, 210, 0.2)",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          textAlign: "center",
                          minHeight: "120px",
                        }}
                      >
                        <QuestionAnswerOutlinedIcon
                          sx={{
                            fontSize: 40,
                            color: "rgba(25, 118, 210, 0.3)",
                            mb: 2,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: isMobile ? 16 : 18,
                            fontWeight: 600,
                            color: "text.secondary",
                            mb: 1,
                          }}
                        >
                          No Questions Available
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.disabled", maxWidth: "300px" }}
                        >
                          This preview doesn't contain any questions yet.
                        </Typography>
                      </Box>
                    ) : (
                      <Grid>
                        {obj?.questions?.map((item, idx) => (
                          <Grid item xs={12} key={idx} sx={{ mb: 2 }}>
                            <Card
                              sx={{
                                borderRadius: "12px",
                                overflow: "hidden",
                                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
                                border: "1px solid rgba(25, 118, 210, 0.1)",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                                  transform: "translateY(-2px)",
                                },
                              }}
                            >
                              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                {/* Question Header */}
                                <Stack
                                  direction={isMobile ? "column" : "row"}
                                  justifyContent="space-between"
                                  alignItems={
                                    isMobile ? "flex-start" : "center"
                                  }
                                  spacing={isMobile ? 1 : 0}
                                  sx={{ mb: 2 }}
                                >
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1.5}
                                  >
                                    <Box
                                      sx={{
                                        width: isMobile ? 28 : 32,
                                        height: isMobile ? 28 : 32,
                                        borderRadius: "8px",
                                        background:
                                          "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          color: "white",
                                          fontWeight: 700,
                                          fontSize: isMobile ? 12 : 14,
                                        }}
                                      >
                                        {idx + 1}
                                      </Typography>
                                    </Box>
                                    <Typography
                                      sx={{
                                        fontSize: isMobile ? 15 : 16,
                                        fontWeight: 700,
                                        color: "text.primary",
                                      }}
                                    >
                                      Question ID: {item?.question_id}
                                    </Typography>
                                  </Stack>
                                </Stack>

                                <Divider sx={{ my: 2, opacity: 0.3 }} />

                                {/* Question Content */}
                                <Box sx={{ mb: 3 }}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 600,
                                      mb: 1,
                                      color: "text.secondary",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <QuestionAnswerOutlinedIcon fontSize="small" />
                                    Question Content
                                  </Typography>
                                  <Box
                                    sx={{
                                      p: 2,
                                      borderRadius: "8px",
                                      backgroundColor:
                                        "rgba(25, 118, 210, 0.02)",
                                      border:
                                        "1px solid rgba(25, 118, 210, 0.08)",
                                    }}
                                  >
                                    <MarkdownQuestion
                                      text={
                                        item.question ||
                                        "No question content available"
                                      }
                                    />
                                    {item.options && (
                                      <Box
                                        sx={{
                                          mt: 3,
                                          pt: 2.5,
                                          borderTop: `1px dashed ${themeColors.border}`,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "16px",
                                            fontWeight: 700,
                                            color: themeColors.textPrimary,
                                            mb: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <Box
                                            component="span"
                                            sx={{
                                              background: themeColors.gradient,
                                              color: "#fff",
                                              px: 1.5,
                                              py: 0.5,
                                              borderRadius: "6px",
                                              fontSize: "14px",
                                            }}
                                          >
                                            Options
                                          </Box>
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: "16px",
                                            lineHeight: 1.8,
                                            color: themeColors.textPrimary,
                                            whiteSpace: "pre-wrap",
                                          }}
                                        >
                                          {item.options}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>

                                {/* Feedback */}
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 600,
                                      mb: 1,
                                      color: "text.secondary",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <DescriptionOutlinedIcon fontSize="small" />
                                    Feedback
                                  </Typography>
                                  <Box
                                    sx={{
                                      p: 2,
                                      borderRadius: "8px",
                                      backgroundColor: "#e8f5e9",
                                      border: `1px solid #a5d6a7`,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        color: "text.primary",
                                        lineHeight: 1.6,
                                      }}
                                    >
                                      {item.feedback || "No feedback provided"}
                                    </Typography>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
};

export default PreviewTable;
